import React, {
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type KeyboardEvent,
	type MouseEvent,
	type SyntheticEvent,
	type ReactNode,
	memo,
	useCallback,
	useRef,
	useEffect,
	type PropsWithChildren,
} from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import noop from 'lodash/noop';
import keycode from 'keycode';
import { AnalyticsContext, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import ChevronDownIconOld from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIconOld from '@atlaskit/icon/glyph/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import Lozenge from '@atlaskit/lozenge';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { colors, typography } from '@atlaskit/theme';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { styledComponentWithCondition } from '@atlassian/jira-compiled-migration/src/ui/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { BACKLOG_CREATE_SPRINT_NUDGE_ID } from '@atlassian/jira-onboarding-quickstart-core/src/common/constants';
import {
	useNudgeStep,
	useIsDismissed,
} from '@atlassian/jira-onboarding-quickstart-core/src/services';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { BACKLOG_COMPLETE_SPRINT_NUDGE_ID } from '@atlassian/jira-software-onboarding-nudges--next/src/common/constants';
import { useRegisterNudgeTarget } from '@atlassian/jira-software-onboarding-nudges--next/src/common/ui/do-not-use-directly-portalled-nudge';
import {
	ConditionalNudgeWrapper,
	type ConditionalNudgeWrapperProps,
} from '@atlassian/jira-software-onboarding-nudges--next/src/ui/conditional-nudge-wrapper/index.tsx';
import { BacklogAccordionNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/scrum-onboarding-tour/backlog-accordion/async.tsx';
import { BacklogCompleteSprintNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/scrum-onboarding-tour/backlog-complete-sprint/async.tsx';
import { BacklogCreateIssueNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/scrum-onboarding-tour/backlog-create-issue/async.tsx';
import { BacklogCreateSprintNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/scrum-onboarding-tour/backlog-create-sprint/async.tsx';
import { BacklogStartSprintNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/scrum-onboarding-tour/backlog-start-sprint/async.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch';
import { HEADER_DROPPABLE_ID_PREFIX } from '../../common/constants';
import { BACKLOG, BOARD, SPRINT } from '../../model/constants';
import {
	type Sprint,
	type CardId,
	type CardListType,
	type CardListId,
	isCardEntryGroup,
	getCardEntryGroupKey,
} from '../../model/types';
import { useBacklogActionCreator, useBacklogDispatch } from '../../state';
import { setRichActiveCard as setRichActiveCardAction } from '../../state/actions/cards/rich-card';
import { setWhiteboardEligibility } from '../../state/actions/whiteboards-entrypoint-experiment';
import { useIsCMPBacklog } from '../../state/state-hooks/capabilities';
import { BacklogButtonVariant } from '../whiteboards-entrypoint-experiment/backlog-button-variant/view';
import {
	useFireExposureEvents,
	useGetWhiteboardEligibleFlow,
} from '../whiteboards-entrypoint-experiment/utils';
import AddSprintDates from './add-sprint-dates';
import CardContainer from './card-container';
import { CardGroup } from './card-group';
import { CardListHeader } from './card-list-header';
import { CardListCheckbox } from './cardlist-checkbox';
import CompleteSprintButton from './complete-sprint-button';
import { COLUMN_LIMIT_TEST_ID, LEFT_SIDE_CONTAINER_TEST_ID } from './constants';
import Divider from './divider';
import Estimations from './estimations';
import FastVirtualCardList from './fast-virtual-list';
import type { RenderRowFn } from './fast-virtual-list/types';
import FirstSprintPlanning from './first-sprint-blank-state/view.tsx';
import { useFormatDate } from './hooks/use-format-date';
import { useBacklogContainerSize, ContainerSizeContext } from './hooks/use-is-compact-view';
import InlineCardCreate from './inline-card-create';
import { LastResultMarker } from './last-result-marker';
import i18nMessages from './messages';
import SprintsMenu from './sprints-menu';
import StartSprintButton from './start-sprint-button-relay';
import TransitionZone from './transition-zone';
import { useCardListDropTarget } from './use-card-list-drop-target';
import { darkerButtonTheme } from './utils';
import WorkLoadByAssignee from './work-load-by-assignee';

const BacklogAccordionNudgeAsyncWithFullWidth = ({
	children,
}: JSX.LibraryManagedAttributes<
	ConditionalNudgeWrapperProps['Wrapper'],
	ComponentProps<ConditionalNudgeWrapperProps['Wrapper']>
>) => (
	<FullWidthWrapper>
		<BacklogAccordionNudgeAsync>{children}</BacklogAccordionNudgeAsync>
	</FullWidthWrapper>
);

// for jsw_sprint_planning_whiteboards experiment,
const SprintPlanningWhiteboards = memo(() => {
	const eligibleFlow = useGetWhiteboardEligibleFlow();
	const dispatch = useBacklogDispatch();
	const fireWhiteboardsExposureEvents = useFireExposureEvents();
	useEffect(() => {
		if (eligibleFlow === 'coUse' || eligibleFlow === 'crossFlow') {
			const cohort = expVal('jsw_sprint_planning_whiteboards', 'cohort', 'not-enrolled');
			fireWhiteboardsExposureEvents(cohort);
			dispatch(
				setWhiteboardEligibility({
					flow: eligibleFlow,
					cohort,
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eligibleFlow]);
	return null;
});

export type Props = {
	cardListId: CardListId;
	type: CardListType;
	typeForAnalytics: string | null;
	name: string;
	sprintData?: Sprint;
	isBacklogEmpty: boolean;
	isFirstList: boolean;
	isFirstNonEmptyList: boolean;
	isLastNonEmptyList?: boolean;
	isIccEnabled: boolean;
	isDisabled: boolean;
	isCollapsed: boolean;
	isActiveSprint: boolean;
	isSprintMenuVisible: boolean;
	isCreateSprintButtonDisabled: boolean;
	isCreateSprintButtonVisible: boolean;
	isStartSprintButtonVisible: boolean;
	isStartSprintEnabled: boolean;
	isCompleteSprintButtonVisible: boolean;
	isCompleteSprintButtonDisabled: boolean;
	isAddSprintDatesButtonVisible: boolean;
	canEditIssue: boolean;
	canEditSprint: boolean;
	cardIds: CardId[];
	allCardIds: CardId[];
	/** The total number of cards excluding subtasks and without filters applied */
	cardCountExcludingSubtasks: number;
	/** The visible number of cards excluding subtasks and accounting for any filters applied */
	visibleCardAmount: number;
	justCreated: boolean;
	onToggle: () => void;
	onCreateSprint: (analyticsEvent: UIAnalyticsEvent) => void;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	minColumnLimit: number | void;
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	maxColumnLimit: number | void;
	showTransitionZone: boolean;
	isWorkloadByAssigneeEnabled: boolean;
};

function CardList({
	cardListId,
	type,
	typeForAnalytics,
	name,
	sprintData,
	isBacklogEmpty = false,
	isFirstList = false,
	isFirstNonEmptyList = false,
	isLastNonEmptyList = false,
	isIccEnabled = false,
	isDisabled = false,
	isCollapsed = false,
	isActiveSprint = false,
	isSprintMenuVisible = false,
	isStartSprintEnabled = false,
	isCreateSprintButtonDisabled = false,
	isCreateSprintButtonVisible = false,
	isAddSprintDatesButtonVisible = false,
	isStartSprintButtonVisible = false,
	isCompleteSprintButtonVisible = false,
	isCompleteSprintButtonDisabled = false,
	canEditIssue = false,
	canEditSprint = false,
	visibleCardAmount = 0,
	justCreated = false,
	onToggle: onToggleProp = noop,
	onCreateSprint: onCreateSprintProp = noop,
	cardIds,
	allCardIds,
	cardCountExcludingSubtasks,
	minColumnLimit,
	maxColumnLimit,
	showTransitionZone,
	isWorkloadByAssigneeEnabled = false,
}: Props) {
	const { formatMessage } = useIntl();
	const isCompanyManaged = useIsCMPBacklog();
	const listRef = useRef<HTMLDivElement | null>(null);
	const backlogContainerSize = useBacklogContainerSize({
		ref: listRef,
	});
	const leftSideRef = useRef<HTMLInputElement | null>(null);
	const isBacklogCardList = type === BACKLOG;
	const [nudgeStep] = useNudgeStep();
	const [isOnboardingDismissed] = useIsDismissed();

	useEffect(() => {
		if (justCreated) {
			leftSideRef?.current?.focus();
		}
	}, [justCreated]);

	const formatDate = useFormatDate();
	const { ref: startSprintButtonNudgeRef } = useRegisterNudgeTarget<HTMLButtonElement>(
		'start-sprint-button',
		isFirstList,
	);

	const isMinLimitReached = !!minColumnLimit && minColumnLimit > allCardIds.length;
	const isMaxLimitReached = !!maxColumnLimit && maxColumnLimit < allCardIds.length;
	const onToggle = (event: MouseEvent<HTMLDivElement>) => {
		event.preventDefault();
		onToggleProp();
	};

	const renderRichCardDelay = 300;
	const setRichActiveCard = useBacklogActionCreator(setRichActiveCardAction);
	const [setRichActiveCardDebounced] = useDebouncedCallback((cardID: number | null) => {
		setRichActiveCard(cardID);
	}, renderRichCardDelay);

	const onHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.keyCode === keycode('enter') || event.keyCode === keycode('space')) {
			event.preventDefault();
			onToggleProp();
		}
	};

	const onCreateSprint = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'createSprint');
		onCreateSprintProp(analyticsEvent);
	};

	const getListName = () => {
		switch (type) {
			case BOARD:
				// Only use name if default name is different
				if (isCompanyManaged) {
					return name;
				}
				return formatMessage(i18nMessages.boardListName);

			case BACKLOG:
				return formatMessage(i18nMessages.backlogListName);

			case SPRINT:
				return name;

			default:
				return '';
		}
	};

	const getEmptyList = () => {
		if (cardIds.length) {
			return formatMessage(i18nMessages.emptyListFiltered);
		}

		switch (type) {
			case BOARD:
				return formatMessage(i18nMessages.emptyBoard);

			case BACKLOG:
				return formatMessage(i18nMessages.emptyBacklog);

			case SPRINT:
				if (isActiveSprint) {
					return formatMessage(i18nMessages.emptyStartedSprint);
				}

				if (isFirstList) {
					return <FirstSprintPlanning />;
				}

				return formatMessage(i18nMessages.emptyNotStartedSprint);

			default:
				return '';
		}
	};

	const chevronRightIcon = fg('tnk-1620-visual-refresh-software-backlog-icons') ? (
		<ChevronRightIcon
			label="expand"
			color={token('color.icon')}
			LEGACY_fallbackIcon={ChevronRightIconOld}
			LEGACY_primaryColor={token('color.icon', '#44546F')}
		/>
	) : (
		<ChevronRightIconOld label="expand" primaryColor={token('color.icon', '#44546F')} />
	);

	const chevronDownIcon = fg('tnk-1620-visual-refresh-software-backlog-icons') ? (
		<ChevronDownIcon
			label="expand"
			color={token('color.icon')}
			LEGACY_fallbackIcon={ChevronDownIconOld}
			LEGACY_primaryColor={token('color.icon', '#44546F')}
		/>
	) : (
		<ChevronDownIconOld label="collapse" primaryColor={token('color.icon', '#44546F')} />
	);

	const renderIcon = () => (isCollapsed ? chevronRightIcon : chevronDownIcon);

	const renderNumberOfVisibleCardsText = () => {
		const totalCardAmountUntruncated = cardCountExcludingSubtasks;
		return totalCardAmountUntruncated !== visibleCardAmount
			? formatMessage(i18nMessages.visibleCardAmount, {
					totalCardAmount: totalCardAmountUntruncated,
					cardAmount: visibleCardAmount,
				})
			: formatMessage(i18nMessages.cardAmount, {
					cardAmount: totalCardAmountUntruncated,
				});
	};

	const renderStartSprintButton = () => {
		const totalCardAmount = cardIds.length;
		const isEmpty = totalCardAmount === 0;
		const shouldShowPrimaryButton = !isEmpty && isFirstList;
		const isStartSprintButtonDisabled = !isStartSprintEnabled || isEmpty;

		const button = (
			<CardListButtonWrapper>
				<StartSprintButton
					// This test-id is being used for the Jira Software NextGen Sprint Lifecycle pollinator test
					testId="software-backlog.card-list.start-sprint-button"
					sprintId={cardListId}
					// @ts-expect-error - TS2769 - No overload matches this call.
					theme={!shouldShowPrimaryButton && !isVisualRefreshEnabled() ? darkerButtonTheme : null}
					appearance={shouldShowPrimaryButton ? 'primary' : 'default'}
					isDisabled={
						fg('jsw-jet-yamato-fe-permissions-seq-sprint-actions')
							? isStartSprintButtonDisabled || !canEditSprint
							: isStartSprintButtonDisabled
					}
					shouldShowDisabledButtonHelper={!canEditSprint}
					ref={startSprintButtonNudgeRef}
				>
					{formatMessage(i18nMessages.startSprint)}
				</StartSprintButton>
			</CardListButtonWrapper>
		);

		const possiblyNudgeWrappedButton = (
			<ConditionalNudgeWrapper
				conditionsToApplyWrapper={isFirstList}
				Wrapper={BacklogStartSprintNudgeAsync}
			>
				{button}
			</ConditionalNudgeWrapper>
		);

		if (
			fg('jsw-jet-yamato-fe-permissions-seq-sprint-actions')
				? isStartSprintButtonDisabled && canEditSprint
				: isStartSprintButtonDisabled
		) {
			return (
				<Tooltip
					content={
						<DisabledStartSprintTooltip>
							{totalCardAmount === 0
								? formatMessage(i18nMessages.disabledStartSprintButtonEmpty)
								: formatMessage(i18nMessages.disabledStartSprintButtonNoParallel)}
						</DisabledStartSprintTooltip>
					}
				>
					{possiblyNudgeWrappedButton}
				</Tooltip>
			);
		}
		return possiblyNudgeWrappedButton;
	};

	const renderCompleteSprintButton = () => (
		<ConditionalNudgeWrapper
			Wrapper={BacklogCompleteSprintNudgeAsync}
			conditionsToApplyWrapper={
				fg('jsw_perf_backlog_complete_sprint_button')
					? nudgeStep === BACKLOG_COMPLETE_SPRINT_NUDGE_ID
					: true
			}
		>
			<CardListButtonWrapper>
				<CompleteSprintButton
					theme={isVisualRefreshEnabled() ? undefined : darkerButtonTheme}
					sprintId={cardListId}
					// This test-id is being used for the Jira Software NextGen Sprint Lifecycle pollinator test
					testId="software-backlog.card-list.complete-sprint-button"
					{...(isCompleteSprintButtonDisabled &&
					fg('jsw-jet-yamato-fe-permissions-seq-sprint-actions')
						? { isDisabled: true }
						: {})}
				>
					{formatMessage(i18nMessages.completeSprint)}
				</CompleteSprintButton>
			</CardListButtonWrapper>
		</ConditionalNudgeWrapper>
	);

	const renderSprintHeader = () => (
		<RightSide
			data-test-id="software-backlog.card-list.estimations-and-actions-container"
			data-testid="software-backlog.card-list.estimations-and-actions-container"
		>
			<Estimations cardListId={cardListId} />
			{isVisualRefreshEnabled() && (
				<Flex alignItems="center" xcss={cardMenuActionsStyles}>
					{isStartSprintButtonVisible && renderStartSprintButton()}
					{isCompleteSprintButtonVisible && renderCompleteSprintButton()}
					{isSprintMenuVisible && (
						<SprintsMenu cardListId={cardListId} cardListType={type} cardListName={name} />
					)}
				</Flex>
			)}
			{!isVisualRefreshEnabled() && (
				<CardMenuActionsWrapper>
					{isStartSprintButtonVisible && renderStartSprintButton()}
					{isCompleteSprintButtonVisible && renderCompleteSprintButton()}
					{isSprintMenuVisible && (
						<SprintsMenu cardListId={cardListId} cardListType={type} cardListName={name} />
					)}
				</CardMenuActionsWrapper>
			)}
		</RightSide>
	);

	const renderCreateSprint = () => (
		<RightSide
			data-test-id="software-backlog.card-list.create-sprint-button-container"
			data-testid="software-backlog.card-list.create-sprint-button-container"
		>
			<ConditionalNudgeWrapper
				Wrapper={BacklogCreateSprintNudgeAsync}
				conditionsToApplyWrapper={
					fg('jsw-perf-onboarding-backlog-create-sprint-load')
						? nudgeStep === BACKLOG_CREATE_SPRINT_NUDGE_ID
						: true
				}
			>
				<Button
					onClick={onCreateSprint}
					isDisabled={isCreateSprintButtonDisabled}
					testId="software-backlog.card-list.create-sprint-button"
					spacing={isVisualRefreshEnabled() ? 'compact' : undefined}
				>
					{formatMessage(i18nMessages.createSprint)}
				</Button>
			</ConditionalNudgeWrapper>
		</RightSide>
	);

	const renderAddSprintDatesButton = () => {
		const stopPropogation = (e: React.MouseEvent | React.KeyboardEvent) => e.stopPropagation();
		return (
			<AddSprintDatesButtonWrapper
				onClick={stopPropogation}
				onKeyDown={stopPropogation}
				data-test-id="software-backlog.card-list.add-sprint-date-button-container"
				data-testid="software-backlog.card-list.add-sprint-date-button-container"
			>
				<AddSprintDates cardListId={Number(cardListId)} />
			</AddSprintDatesButtonWrapper>
		);
	};

	const renderSprintData = () => {
		if (!sprintData) {
			return null;
		}

		const { goal } = sprintData;

		return <div>{goal && <GoalWrapper>{goal}</GoalWrapper>}</div>;
	};

	const renderColumnLimits = () =>
		isFirstList && (
			<>
				{minColumnLimit ? (
					<ColumnLimit data-testid={COLUMN_LIMIT_TEST_ID}>
						<Lozenge
							appearance={isMinLimitReached ? 'moved' : 'default'}
							isBold={isMinLimitReached}
						>{`MIN ${minColumnLimit}`}</Lozenge>
					</ColumnLimit>
				) : null}
				{maxColumnLimit ? (
					<ColumnLimit data-testid={COLUMN_LIMIT_TEST_ID}>
						<Lozenge
							appearance={isMaxLimitReached ? 'removed' : 'default'}
							isBold={isMaxLimitReached}
						>{`MAX ${maxColumnLimit}`}</Lozenge>
					</ColumnLimit>
				) : null}
			</>
		);

	const renderLeftSide = () => (
		<LeftSide
			data-testid={LEFT_SIDE_CONTAINER_TEST_ID}
			role="button"
			tabIndex={0}
			aria-controls={`backlog-accordion-${cardListId}`}
			onClick={onToggle}
			onKeyDown={onHeaderKeyDown}
			aria-expanded={!isCollapsed}
			ref={leftSideRef}
		>
			{isVisualRefreshEnabled() ? (
				<Box xcss={chevronIconStyles}>{renderIcon()}</Box>
			) : (
				<Icon>{renderIcon()}</Icon>
			)}
			<Flex alignItems="baseline">
				<>
					<ListName>{getListName()}</ListName>
					{sprintData &&
						!isAddSprintDatesButtonVisible &&
						sprintData.startDate &&
						sprintData.endDate && (
							<DatesWrapper>
								{`${formatDate(sprintData.startDate)} – ${formatDate(sprintData.endDate)}`}
							</DatesWrapper>
						)}
					{sprintData && isAddSprintDatesButtonVisible && renderAddSprintDatesButton()}
					<VisibleCardsInCardListHeader>
						({renderNumberOfVisibleCardsText()})
					</VisibleCardsInCardListHeader>

					<span data-testid={`${LEFT_SIDE_CONTAINER_TEST_ID}-${type}`}>
						<ScreenReaderText aria-live="polite" role="status">
							{getListName()} {renderNumberOfVisibleCardsText()}
						</ScreenReaderText>
					</span>

					{renderColumnLimits()}
				</>
			</Flex>
		</LeftSide>
	);

	const renderHeader = () => (
		<CardListHeaderContainer
			isMinLimitReached={isMinLimitReached}
			isMaxLimitReached={isMaxLimitReached}
			isVisualRefreshEnabled={isVisualRefreshEnabled()}
		>
			<CardListCheckbox
				cardListId={cardListId}
				isDisabled={isDisabled}
				{...(fg('a11y_violations_fixes')
					? {
							listName: `${type === SPRINT ? `${formatMessage(i18nMessages.sprintListNamePrefix)} ` : ''}${getListName()}`,
						}
					: {})}
			/>
			<ConditionalNudgeWrapper
				Wrapper={BacklogAccordionNudgeAsyncWithFullWidth}
				conditionsToApplyWrapper={
					(fg('jsw-perf-backlog-lazy-instrumented-nudge-spotlight')
						? !isOnboardingDismissed
						: true) && type === BACKLOG
				}
			>
				<HeadingWrapper>{renderLeftSide()}</HeadingWrapper>
			</ConditionalNudgeWrapper>
			{renderSprintHeader()}
			{type === BACKLOG && <BacklogButtonVariant />}
			{isCreateSprintButtonVisible && renderCreateSprint()}
		</CardListHeaderContainer>
	);

	const renderIccCard: RenderRowFn = useCallback(
		(cardEntry, row, isScrolling) => {
			if (isCardEntryGroup(cardEntry)) {
				return (
					<CardGroup
						key={getCardEntryGroupKey(cardEntry, cardListId)}
						isScrolling={isScrolling}
						row={row}
						cardListId={cardListId}
						cardGroup={cardEntry}
						setRichActiveCard={setRichActiveCardDebounced}
					/>
				);
			}

			return (
				<CardContainer
					cardListId={cardListId}
					cardId={cardEntry.cardId}
					row={row}
					isScrolling={isScrolling}
					isAttachedToParent={false}
					setRichActiveCard={setRichActiveCardDebounced}
				/>
			);
		},
		[cardListId, setRichActiveCardDebounced],
	);

	const renderA11yScreenReaderInfo = () =>
		justCreated && (
			<span data-testid={`${HEADER_DROPPABLE_ID_PREFIX}${cardListId}`}>
				<ScreenReaderText aria-live="assertive" id={`${HEADER_DROPPABLE_ID_PREFIX}${cardListId}`}>
					{formatMessage(i18nMessages.createdSprintAriaLive, {
						sprintName: getListName(),
					})}
				</ScreenReaderText>
			</span>
		);

	const isHeaderDropDisabled = !isCollapsed || isDisabled;

	const isDividerHidden =
		!canEditIssue || type === BACKLOG || isActiveSprint || (isCollapsed && type === SPRINT);

	const headerRef = useRef<HTMLDivElement>(null);
	const transitionZoneElementRef = useRef<HTMLDivElement>(null);
	const { isDraggingOver: isDraggingOverPragmatic } = useCardListDropTarget({
		headerElementRef: headerRef,
		transitionZoneElementRef,
		cardListId,
		isHeaderDropDisabled,
	});

	const header = (
		<CollapsedDropzoneWrapper ref={headerRef}>
			{!isHeaderDropDisabled && isDraggingOverPragmatic && <CollapsedDropzone />}
			{!isVisualRefreshEnabled() && renderHeader()}
			{isVisualRefreshEnabled() && (
				<CardListHeader
					isCreateSprintButtonVisible={isCreateSprintButtonVisible}
					isDisabled={isDisabled}
					isMaxLimitReached={isMaxLimitReached}
					isMinLimitReached={isMinLimitReached}
					onCreateSprint={onCreateSprintProp}
					onToggle={onToggleProp}
				/>
			)}
		</CollapsedDropzoneWrapper>
	);

	const list = (
		<ContainerSizeContext.Provider value={{ size: backlogContainerSize }}>
			<FastVirtualCardList
				cardListId={cardListId}
				renderCard={renderIccCard}
				// Let the transition zone handle the drop when it is visible
				isDropDisabled={isDisabled || showTransitionZone}
				emptyList={getEmptyList()}
			/>
		</ContainerSizeContext.Provider>
	);

	const iccAriaLabelledById = `icc-${name.replace(/\s/g, '-')}`.toLowerCase();

	const icc = isIccEnabled ? (
		<InlineCardCreate
			listId={cardListId}
			isOpenByDefault={isFirstList && isBacklogEmpty}
			ariaLabelledBy={iccAriaLabelledById}
		/>
	) : null;

	const iccWithNudge = () => {
		if (!fg('jsw-perf-async-backlogcreateissuenudgeasync-load')) {
			return (
				<ConditionalNudgeWrapper
					Wrapper={BacklogCreateIssueNudgeAsync}
					conditionsToApplyWrapper={isBacklogCardList}
				>
					{icc}
				</ConditionalNudgeWrapper>
			);
		}

		if (!isIccEnabled) {
			return null;
		}

		return (
			<ConditionalNudgeWrapper
				Wrapper={BacklogCreateIssueNudgeAsync}
				conditionsToApplyWrapper={!!nudgeStep && isBacklogCardList}
			>
				<InlineCardCreate
					listId={cardListId}
					isOpenByDefault={isFirstList && isBacklogEmpty}
					ariaLabelledBy={iccAriaLabelledById}
				/>
			</ConditionalNudgeWrapper>
		);
	};

	const workloadByAssignee = () =>
		!isBacklogCardList && isCompanyManaged && isWorkloadByAssigneeEnabled ? (
			<WorkLoadByAssignee cardListId={cardListId} sprintName={name} />
		) : null;

	return (
		<AnalyticsContext
			data={{
				attributes: {
					cardListType: typeForAnalytics,
				},
			}}
		>
			<Container
				hasSpaceBetweenCardLists={canEditIssue ? isActiveSprint && !isCollapsed : false}
				isBacklogCardList={isVisualRefreshEnabled() ? false : isBacklogCardList}
				isVisualRefreshEnabled={isVisualRefreshEnabled()}
				isDisabled={isDisabled}
				aria-controls={`${HEADER_DROPPABLE_ID_PREFIX}${cardListId}`}
				data-testid={`software-backlog.card-list.container.${cardListId}`}
			>
				{renderA11yScreenReaderInfo()}
				{header}
				{/* for jsw_sprint_planning_whiteboards experiment, renders nothing but can be
				conditionally called without breaking the rules of hooks */}
				{isBacklogCardList && fg('sprint_planning_whiteboards_kill_switch') && (
					<SprintPlanningWhiteboards />
				)}
				{!isCollapsed && renderSprintData()}
				{!isCollapsed && workloadByAssignee()}
				{!isCollapsed && (
					<ListContainer
						id={`backlog-accordion-${cardListId}`}
						data-testid="software-backlog.card-list.accordion"
						innerRef={listRef}
						isFirstNonEmptyList={isFirstNonEmptyList}
					>
						<TransitionZone isVisible={showTransitionZone} ref={transitionZoneElementRef} />
						{list}
						{iccWithNudge()}
						<VisuallyHidden as="p" id={iccAriaLabelledById}>
							{formatMessage(i18nMessages.inlineCardCreateAriaLabel, {
								sprintName: getListName(),
							})}
						</VisuallyHidden>
					</ListContainer>
				)}
				{isLastNonEmptyList && <LastResultMarker />}
			</Container>
			{!isDividerHidden && <Divider id={cardListId} />}
		</AnalyticsContext>
	);
}

export default memo(CardList);

interface LimitReached {
	isMinLimitReached: boolean;
	isMaxLimitReached: boolean;
	isVisualRefreshEnabled: boolean;
}

type ContainerProps = {
	isDisabled: boolean;
	isBacklogCardList: boolean;
	isVisualRefreshEnabled: boolean;
	hasSpaceBetweenCardLists: boolean;
	children: ReactNode;
	'aria-controls': string;
};

const ListNameExperiment = ({ children }: PropsWithChildren<{}>) => (
	<Heading as="div" level="h400">
		{children}
	</Heading>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled2.div<ContainerProps>({
	minWidth: '626px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	margin: (props: ContainerProps) =>
		props.hasSpaceBetweenCardLists
			? `0 0 ${token('space.300', '24px')}`
			: `0 0 ${token('space.100', '8px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props: ContainerProps) => (props.isDisabled ? 0.5 : undefined),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	pointerEvents: (props: ContainerProps) => (props.isDisabled ? 'none' : undefined),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props: ContainerProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		!props.isBacklogCardList ? token('elevation.surface.sunken', colors.N20) : undefined,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderRadius: (props) =>
		props.isVisualRefreshEnabled ? token('space.075') : token('space.100', '8px'),
	padding: `${token('space.100', '8px')} ${token('space.100', '8px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardListHeaderControl = styled.div<LimitReached>(
	{
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		minHeight: `${gridSize * 4}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isMinLimitReached, isMaxLimitReached }) =>
		(isMinLimitReached || isMaxLimitReached) &&
		`
            background-color: ${
							isMaxLimitReached
								? token('color.background.accent.red.subtlest', '#FFEDEB')
								: token('color.background.accent.yellow.subtlest', '#FFF7D6')
						};
            /* negative margin to fill out the header correctly */
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
            margin: -${gridSize}px -${gridSize * 1.5}px;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
            padding: ${gridSize}px ${gridSize * 1.5}px;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
            border-top-left-radius: ${gridSize}px;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
            border-top-right-radius: ${gridSize}px;
        `,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const CardListHeaderExperiment = styled2.div<LimitReached>(
	{
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		minHeight: (props) =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			props.isVisualRefreshEnabled ? `${token('space.500')}` : `${gridSize * 4}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isMinLimitReached, isMaxLimitReached }) =>
		(isMinLimitReached || isMaxLimitReached) &&
		`
            background-color: ${
							isMaxLimitReached
								? token('color.background.accent.red.subtlest', '#FFEDEB')
								: token('color.background.accent.yellow.subtlest', '#FFF7D6')
						};
            /* negative margin to fill out the header correctly */
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
            margin: -${gridSize}px -${gridSize * 1.5}px;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
            padding: ${gridSize}px ${gridSize * 1.5}px;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
            border-top-left-radius: ${gridSize}px;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
            border-top-right-radius: ${gridSize}px;
        `,
);

const CardListHeaderContainer = styledComponentWithCondition(
	() => ff('compiled.migration.jsw.tanuki'),
	CardListHeaderExperiment,
	CardListHeaderControl,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardMenuActionsWrapper = styled2.div({
	// TODO Delete this comment after verifying space token -> previous value ``${gridSize}px``
	paddingLeft: token('space.100', '8px'),
	display: 'flex',
	flexDirection: 'row',
	'&:empty': {
		display: 'none',
	},
});

const cardMenuActionsStyles = xcss({
	paddingLeft: 'space.100',
	':empty': {
		display: 'none',
	},
});

type ListContainerProps = {
	isFirstNonEmptyList: boolean;
};

// TODO Delete this comment after verifying space token -> previous value `margin: 8px`
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListContainerControl = styled.div<ListContainerProps>(
	{
		margin: `${token('space.100', '8px')} 0 0 0`,
		position: 'relative',
	},
	/* to correctly show the spotlight, to be removed when remove UIF changeboarding */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isFirstNonEmptyList }: ListContainerProps) =>
		isFirstNonEmptyList &&
		`
        *::before,
        *::after {
            z-index: 9;
        }`,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const ListContainerExperiment = styled2.div<ListContainerProps>({
	margin: `${token('space.100', '8px')} 0 0 0`,
	position: 'relative',
});

const ListContainer = styledComponentWithCondition(
	() => ff('compiled.migration.jsw.tanuki'),
	ListContainerExperiment,
	ListContainerControl,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Icon = styled2.div({
	pointerEvents: 'none',
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListNameControl = styled.div`
	${typography.h400()};
	margin: 0;
`;

const ListName = styledComponentWithCondition(
	() => ff('compiled.migration.jsw.tanuki'),
	ListNameExperiment,
	ListNameControl,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VisibleCardsInCardListHeader = styled2.div({
	fontSize: '0.75rem',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N200),
	fontWeight: 'normal',
	margin: `${token('space.0', '0px')} ${token('space.0', '0px')} ${token('space.0', '0px')} ${token('space.100', '8px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColumnLimit = styled2.div({
	fontWeight: 'normal',
	margin: `${token('space.0', '0px')} ${token('space.0', '0px')} ${token('space.0', '0px')} ${token('space.100', '8px')}`,
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CollapsedDropzoneWrapper = styled2.div({
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CollapsedDropzoneControl = styled.div({
	position: 'absolute',
	width: '100%',
	height: '100%',
	border: `2px dashed ${token('color.border', '#dfe1e6')}`,
	borderRadius: "'3px'",
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const CollapsedDropzoneExperiment = styled2.div({
	position: 'absolute',
	width: '100%',
	height: '100%',
	border: `2px dashed ${token('color.border', '#dfe1e6')}`,
	borderRadius: "'3px'",
});

const CollapsedDropzone = styledComponentWithCondition(
	() => ff('compiled.migration.jsw.tanuki'),
	CollapsedDropzoneExperiment,
	CollapsedDropzoneControl,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LeftSideOld = styled2.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	cursor: 'pointer',
	flex: 1,
	marginTop: 0,
	marginRight: token('space.100', '8px'),
	marginBottom: 0,
	marginLeft: 0,

	'&:focus': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		boxShadow: `0 0 0 ${gridSize / 4}px ${token('color.border.focused', colors.B200)}`,
		outline: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LeftSideNew = styled2.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	cursor: 'pointer',
	flex: 1,
	marginTop: 0,
	marginRight: token('space.100', '8px'),
	marginBottom: 0,
	marginLeft: 0,
	gap: token('space.050'),

	'&:focus-visible': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		boxShadow: `0 0 0 ${gridSize / 4}px ${token('color.border.focused', colors.B200)}`,
		outline: 'none',
	},
});

const LeftSide = componentWithCondition(isVisualRefreshEnabled, LeftSideNew, LeftSideOld);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RightSide = styled2.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	// TODO Delete this comment after verifying space token -> previous value ``${gridSize}px``
	paddingLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DisabledStartSprintTooltip = styled2.div({
	// TODO Delete this comment after verifying space token -> previous value ``${gridSize / 2}px``
	padding: token('space.050', '4px'),
	textAlign: 'center',
	lineHeight: 1.25,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoalWrapper = styled2.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body.small', fontFallback.body.small),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N200),
	margin: token('space.0', '0px'),
	marginTop: token('space.025', '2px'),
	paddingLeft: token('space.200', '16px'),
	paddingRight: token('space.250', '20px'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DatesWrapper = styled2.div({
	fontSize: '0.75rem',
	fontWeight: 'normal',
	margin: `${token('space.0', '0px')} ${token('space.0', '0px')} ${token('space.0', '0px')} ${token('space.100', '8px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardListButtonWrapper = styled2.div({
	// TODO Delete this comment after verifying space token -> previous value ``${gridSize}px``
	marginRight: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddSprintDatesButtonWrapper = styled2.div({
	margin: `${token('space.0', '0px')} ${token('space.0', '0px')} ${token('space.0', '0px')}
        ${token('space.100', '8px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullWidthWrapper = styled2.div({
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingWrapper = styled2.h2({
	display: 'contents',
	font: 'unset',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VisuallyHidden = styled2.div({
	clip: 'rect(0 0 0 0)',
	clipPath: 'inset(50%)',
	height: '1px',
	overflow: 'hidden',
	position: 'absolute',
	whiteSpace: 'nowrap',
	width: '1px',
});

const chevronIconStyles = xcss({
	pointerEvents: 'none',
	width: '24px',
	height: '24px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});
